// === WebSocket Connection ===
// connect / send / scheduleReconnect
// 依赖注入模式避免与 session/render 模块的循环引用

import { connectionState } from '../state';
import { WS_URL, RECONNECT_MAX_ATTEMPTS } from '../constants';
import { showToast } from '../ui/toast';
import { logCodexBubbleDebug, logCodexSteeringDebug, logConnectionDebug } from '../ui/session-debug';
import { composeState as stateComposeState, sessionState as stateSessionState } from '../state';

// 注入回调（由 app.ts 在初始化时设置）
let _handleMessage: ((msg: unknown) => void) | null = null;
let _clearSessionLoading: (() => void) | null = null;
let _appendError: ((msg: string, opts?: { type?: string; dismissible?: boolean }) => void) | null = null;
let _getPendingAuthPassword: (() => string | null) | null = null;
let connectStartedAt = 0;
let authStartedAt = 0;

/** 在 app 初始化时注入需要的回调，避免循环依赖 */
export function setupConnectionHandlers(handlers: {
  handleMessage: (msg: unknown) => void;
  clearSessionLoading: () => void;
  appendError: (msg: string, opts?: { type?: string; dismissible?: boolean }) => void;
  getPendingAuthPassword: () => string | null;
}): void {
  _handleMessage = handlers.handleMessage;
  _clearSessionLoading = handlers.clearSessionLoading;
  _appendError = handlers.appendError;
  _getPendingAuthPassword = handlers.getPendingAuthPassword;
}

export function send(data: unknown): void {
  if ((data as { type?: unknown })?.type === 'steer') {
    const steerData = data as { sessionId?: string; clientMessageId?: string; text?: string };
    logCodexSteeringDebug('client:ws-send', {
      sessionId: steerData.sessionId || null,
      clientMessageId: steerData.clientMessageId || null,
      textLength: typeof steerData.text === 'string' ? steerData.text.length : 0,
      currentSessionId: stateSessionState.currentSessionId || null,
      currentSessionRunning: !!stateSessionState.currentSessionRunning,
      isGenerating: !!stateComposeState.isGenerating,
    });
  }
  if (connectionState.ws && connectionState.ws.readyState === WebSocket.OPEN) {
    connectionState.ws.send(JSON.stringify(data));
  } else {
    logConnectionDebug('send-skipped', {
      readyState: connectionState.ws?.readyState ?? null,
      messageType: (data as { type?: unknown })?.type || null,
    });
    showToast('连接已断开，正在重连…');
  }
}

export function scheduleReconnect(): void {
  if (connectionState.reconnectTimer) {
    logConnectionDebug('reconnect-skipped', { reason: 'already-scheduled' });
    return;
  }
  if (connectionState.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
    logConnectionDebug('reconnect-give-up', { attempts: connectionState.reconnectAttempts });
    _appendError?.('连接已断开且重连失败，请刷新页面重试。', { type: 'connection', dismissible: false });
    return;
  }
  if (navigator.onLine === false) {
    logConnectionDebug('reconnect-waiting-offline', { attempts: connectionState.reconnectAttempts });
    showToast('网络已断开，等待恢复…');
    return;
  }

  document.body.classList.add('ws-reconnecting');

  const delay = Math.min(1000 * Math.pow(2, connectionState.reconnectAttempts), 30000);
  connectionState.reconnectAttempts++;
  logConnectionDebug('reconnect-scheduled', {
    attempt: connectionState.reconnectAttempts,
    delayMs: delay,
    maxAttempts: RECONNECT_MAX_ATTEMPTS,
  });

  if (connectionState.reconnectAttempts <= 3 || connectionState.reconnectAttempts % 3 === 0) {
    showToast(`正在重连 (${connectionState.reconnectAttempts}/${RECONNECT_MAX_ATTEMPTS})…`);
  }

  connectionState.reconnectTimer = setTimeout(() => {
    connectionState.reconnectTimer = null;
    logConnectionDebug('reconnect-attempt', { attempt: connectionState.reconnectAttempts });
    connect();
  }, delay);
}

export function connect(): void {
  if (
    connectionState.ws &&
    (connectionState.ws.readyState === WebSocket.CONNECTING ||
      connectionState.ws.readyState === WebSocket.OPEN)
  ) return;

  logConnectionDebug('connect-start', {
    reconnectAttempt: connectionState.reconnectAttempts,
    hadSocket: !!connectionState.ws,
  });
  connectStartedAt = Date.now();
  connectionState.ws = new WebSocket(WS_URL);

  connectionState.ws.onopen = () => {
    const wasReconnecting = document.body.classList.contains('ws-reconnecting');
    logConnectionDebug('open', {
      wasReconnecting,
      reconnectAttempt: connectionState.reconnectAttempts,
      durationMs: Date.now() - connectStartedAt,
    });
    connectionState.reconnectAttempts = 0;
    if (wasReconnecting) {
      document.body.classList.remove('ws-reconnecting');
      showToast('连接已恢复');
    }
    if (connectionState.authToken) {
      authStartedAt = Date.now();
      logConnectionDebug('auth-send', { method: 'token' });
      send({ type: 'auth', token: connectionState.authToken });
    } else {
      const pending = _getPendingAuthPassword?.() ?? null;
      if (pending !== null) {
        authStartedAt = Date.now();
        logConnectionDebug('auth-send', { method: 'password' });
        send({ type: 'auth', password: pending });
      }
    }
  };

  connectionState.ws.onmessage = (e) => {
    let msg: unknown;
    try { msg = JSON.parse(e.data); } catch { return; }
    const typedMessage = msg as { type?: string; sessionId?: string; text?: string };
    if (typedMessage.type === 'session_info') {
      const sessionInfo = msg as { messages?: Array<{ role?: string; clientMessageId?: string; timestamp?: string; content?: string }>; updated?: string; isRunning?: boolean; historyTotal?: number; historyBuffered?: number };
      const messages = Array.isArray(sessionInfo.messages) ? sessionInfo.messages : [];
      logCodexSteeringDebug('client:session-info-received-raw', {
        sessionId: typedMessage.sessionId || null,
        updated: sessionInfo.updated || null,
        isRunning: !!sessionInfo.isRunning,
        messageCount: messages.length,
        userMessageCount: messages.filter((message) => message?.role === 'user').length,
        historyTotal: Number.isFinite(sessionInfo.historyTotal) ? sessionInfo.historyTotal : null,
        historyBuffered: Number.isFinite(sessionInfo.historyBuffered) ? sessionInfo.historyBuffered : null,
        messageIdentities: messages.slice(-6).map((message) => ({
          role: message?.role || null,
          identity: message?.clientMessageId || message?.timestamp || null,
          contentLength: String(message?.content || '').length,
        })),
      });
    }
    if (typedMessage.type === 'codex_turn_boundary' || typedMessage.type === 'text_delta') {
      logCodexBubbleDebug('message-received', {
        type: typedMessage.type,
        sessionId: typedMessage.sessionId || null,
        textLength: typeof typedMessage.text === 'string' ? typedMessage.text.length : 0,
      });
    }
    _handleMessage?.(msg);
  };

  connectionState.ws.onclose = () => {
    logConnectionDebug('close', { reconnectAttempt: connectionState.reconnectAttempts });
    _clearSessionLoading?.();
    scheduleReconnect();
  };

  connectionState.ws.onerror = () => {
    logConnectionDebug('error', { reconnectAttempt: connectionState.reconnectAttempts });
    if (connectionState.reconnectAttempts === 0) {
      document.body.classList.add('ws-reconnecting');
    }
  };
}

export function getAuthStartedAt(): number {
  // 供消息处理层计算认证响应耗时，避免跨模块共享可变状态。
  return authStartedAt;
}

export function waitForSocketOpen(timeoutMs = 8000): Promise<void> {
  const existing = connectionState.ws;
  if (existing?.readyState === WebSocket.OPEN) return Promise.resolve();

  connect();

  return new Promise((resolve, reject) => {
    const ws = connectionState.ws;
    if (!ws) {
      reject(new Error('无法建立连接'));
      return;
    }
    if (ws.readyState === WebSocket.OPEN) {
      resolve();
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      timer = null;
      cleanup();
      reject(new Error('连接超时，请稍后重试'));
    }, timeoutMs);

    function cleanup(): void {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      ws.removeEventListener('open', handleOpen);
      ws.removeEventListener('close', handleClose);
      ws.removeEventListener('error', handleError);
    }

    function handleOpen(): void {
      cleanup();
      resolve();
    }

    function handleClose(): void {
      cleanup();
      reject(new Error('连接已断开，正在重连'));
    }

    function handleError(): void {
      cleanup();
      reject(new Error('连接失败，请稍后重试'));
    }

    ws.addEventListener('open', handleOpen, { once: true });
    ws.addEventListener('close', handleClose, { once: true });
    ws.addEventListener('error', handleError, { once: true });
  });
}
